import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  PersonAdd,
  PersonSearch,
  DeleteOutline,
  TravelExplore as TravelExploreIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  OpenInNew as OpenInNewIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Payments as PaymentsIcon,
  SwapHoriz as SwapHorizIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import FacturaPOSClienteSearchBar from '../../facturaventa/pos/FacturaPOSClienteSearchBar';
import FacturaPOSNuevoClienteDialog from '../../facturaventa/pos/FacturaPOSNuevoClienteDialog';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../contexts/AuthContext';
import { ROUTES } from '../../../config/routes';
import { useCan } from '../../../hooks/useCan';
import api from '../../../services/api';
import clienteService from '../../../services/clienteService';
import facturaVentaService, { resolveDetalleMasterKeys, resolveMasterNumber } from '../../../services/facturaVentaService';
import ventasSeguimientoService from '../../../services/ventasSeguimientoService';
import sucVentaService from '../../../services/sucVentaService';
import remitoVentaService, { type RemitoVentaCreateDto } from '../../../services/remitoVentaService';
import { resolverPrecioListaDetalle } from '../../../utils/normalizarDetallesFacturaVenta';
import {
  cargarMapaSucVentaPorClave,
  cargarMapaUbicacionNroSuc,
  facturaInputDesdeFacturaVenta,
  resolverContextoSucVentaImpresion,
} from '../../../utils/sucVentaImpresionResolver';
import { cantidadLineaAUmStock, TIPO_NC_HILO, TIPO_REMITO_HILO } from '../../../utils/remitoPendienteUm';
import pedidoVentaService from '../../../services/pedidoVentaService';
import cotizacionVentaService from '../../../services/cotizacionVentaService';
import notaCreditoVentaService from '../../../services/notaCreditoVentaService';
import notaDebitoVentaService from '../../../services/notaDebitoVentaService';
import tesoreriaService from '../../../services/tesoreriaService';
import PDFViewer from '../../../components/shared/PDFViewer';
import { loadInfo360Tab, saveInfo360Tab } from '../../../utils/info360TabStorage';
import { formatCurrency } from '../../../utils/formatters';
import { getApiErrorMessage } from '../../../utils/apiError';
import { fetchFacturaVentaPdf } from '../../../utils/facturaVentaPdf';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import AplicarSaldoReciboDialog from '../../../components/Tesoreria/AplicarSaldoReciboDialog';
import ConfirmarEnvioFacturaEmailDialog from '../../../components/ventas/ConfirmarEnvioFacturaEmailDialog';
import CotizacionesVenta from '../../cotizacionventa';
import PedidosVenta from '../../pedidoventa';
import NuevaFacturaPOS from '../../facturaventa/NuevaFacturaPOS';
import RemitoVentaForm, { type RemitoParcialDesdeFactura } from '../../ventas/RemitoVentaForm';
import NuevaNotaCreditoPOS from '../../notacreditoventa/NuevaNotaCreditoPOS';
import NuevaNotaDebitoPOS from '../../notadebitoventa/NuevaNotaDebitoPOS';
import NuevoRecibo from '../../../components/Tesoreria/NuevoRecibo';
import { GestionCerrarContext } from './gestionCerrarContext';
import type { Info360Severidad } from '../info360/types';
import type {
  AccionNotaItem,
  ClienteGestion,
  ClienteGestionDocumento,
  ClienteGestionTelefonoMatch,
  ClienteNotaLlamada,
} from './types';

const MODOS = ['vender', 'cobrar'] as const;
type Modo = (typeof MODOS)[number];

type CreditoAplicar = {
  nroFormulario: number;
  tipoCodigo: string;
  numero: string;
  importe: number;
};

type Operacion =
  | { tipo: 'cotizacion' }
  | { tipo: 'pedido' }
  | { tipo: 'factura'; pedidoId?: number; nroFormulario?: number; cotizacionId?: number }
  | { tipo: 'remito'; parcial?: RemitoParcialDesdeFactura }
  | { tipo: 'nc' }
  | { tipo: 'nd' }
  | { tipo: 'recibo'; nroFormulario?: number };

type PdfVista = {
  url: string;
  title: string;
  documentId: string;
  documentType: 'pedido' | 'recibo' | 'nota-credito' | 'nota-debito' | 'factura' | 'cotizacion' | 'remito';
};

const hoyMas = (dias: number): string => {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() + dias);
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${fecha.getFullYear()}-${mes}-${dia}`;
};

const fechaCorta = (iso?: string | null): string => {
  if (!iso) return '';
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return '';
  return fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fechaHoraNota = (fecha?: string | null, hora?: string | null): string => {
  const base = fecha ? new Date(fecha) : null;
  if (!base || Number.isNaN(base.getTime())) return '';
  const dia = base.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  const origenHora = hora ? new Date(hora) : base;
  const hhmm = Number.isNaN(origenHora.getTime())
    ? ''
    : origenHora.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  return hhmm ? `${dia} ${hhmm}` : dia;
};

const abrirPestana = (path: string) => {
  window.open(path, '_blank', 'noopener,noreferrer');
};

const colorSemaforoInfo360 = (severidad: Info360Severidad | null): string => {
  switch (severidad) {
    case 'danger':
      return '#d32f2f';
    case 'warning':
      return '#ed6c02';
    case 'ok':
      return '#2e7d32';
    case 'info':
      return '#0288d1';
    default:
      return '#757575';
  }
};

const coloresAccion: Record<string, [string, string]> = {
  Imprimir: ['#2196f3', '#1976d2'],
  Facturar: ['#4caf50', '#45a049'],
  Remitir: ['#795548', '#6d4c41'],
  Aplicar: ['#2e7d32', '#1b5e20'],
  Cobrar: ['#2e7d32', '#1b5e20'],
  Reenviar: ['#0288d1', '#01579b'],
};

const FilaDocumento: React.FC<{
  doc: ClienteGestionDocumento;
  acciones: React.ReactNode;
  onAbrir?: () => void;
}> = ({ doc, acciones, onAbrir }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={1}
    justifyContent="space-between"
    alignItems={{ xs: 'stretch', sm: 'center' }}
    sx={{ py: 1 }}
  >
    <Box>
      <Typography variant="body2" fontWeight={600} component="div">
        {onAbrir ? (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onAbrir}
            aria-label={`Abrir ${doc.tipo} ${doc.numero}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              font: 'inherit',
              textAlign: 'left',
            }}
          >
            {doc.tipo} {doc.numero}
            <OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden />
          </Link>
        ) : (
          `${doc.tipo} ${doc.numero}`
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {fechaCorta(doc.fecha)}
        {doc.fechaVencimiento ? ` · Vence ${fechaCorta(doc.fechaVencimiento)}` : ''}
        {doc.estado ? ` · ${doc.estado}` : ''}
      </Typography>
    </Box>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {formatCurrency(doc.saldo || doc.importe)}
      </Typography>
      {acciones}
    </Stack>
  </Stack>
);

const GestionClientes: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user, tenantId } = useAuth();

  const puedePedido = useCan('ventas.pedidos.nuevo');
  const puedeCotizacion = useCan('ventas.cotizaciones.nuevo');
  const puedeFactura = useCan('ventas.facturas.nuevo');
  const puedeRemito = useCan('ventas.remitos.nuevo');
  const puedeNc = useCan('ventas.notas-credito.nuevo');
  const puedeNd = useCan('ventas.notas-debito.nuevo');
  const puedeNota = useCan('ventas.clientes.gestion.nuevo');

  const nroClienteQuery = Number(searchParams.get('nroCliente'));
  const tel = searchParams.get('tel') ?? '';
  const nroCliente = Number.isFinite(nroClienteQuery) && nroClienteQuery > 0 ? nroClienteQuery : null;

  const [modo, setModo] = useState<Modo>(() =>
    loadInfo360Tab('gestion', user?.username, tenantId, MODOS, 'vender'),
  );
  const [buscador, setBuscador] = useState(false);
  const [nuevoClienteAbierto, setNuevoClienteAbierto] = useState(false);
  const [resolviendo, setResolviendo] = useState(false);
  const [matches, setMatches] = useState<ClienteGestionTelefonoMatch[]>([]);
  const [cargando, setCargando] = useState(false);
  const [gestion, setGestion] = useState<ClienteGestion | null>(null);
  const [accionesNota, setAccionesNota] = useState<AccionNotaItem[]>([]);
  const [alcanceNotas, setAlcanceNotas] = useState<'3' | '10' | 'todas'>('3');
  const [notas, setNotas] = useState<ClienteNotaLlamada[]>([]);
  const [cargandoNotas, setCargandoNotas] = useState(false);
  const [idAccion, setIdAccion] = useState('');
  const [textoNota, setTextoNota] = useState('');
  const [contactoNota, setContactoNota] = useState('');
  const [fechaProx, setFechaProx] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [notaFormAbierta, setNotaFormAbierta] = useState(false);
  const [creditoAplicar, setCreditoAplicar] = useState<CreditoAplicar | null>(null);
  const [aplicarSaldosAbierto, setAplicarSaldosAbierto] = useState(false);
  const [reenviar, setReenviar] = useState<ClienteGestionDocumento | null>(null);
  const [enviandoMail, setEnviandoMail] = useState(false);
  const [pdf, setPdf] = useState<PdfVista | null>(null);
  const [abriendoPdf, setAbriendoPdf] = useState<number | null>(null);
  const [operacion, setOperacion] = useState<Operacion | null>(null);
  const [remitiendo, setRemitiendo] = useState<number | null>(null);
  const [remitoMenu, setRemitoMenu] = useState<{ el: HTMLElement; facturaId: number } | null>(null);
  const [paginasLista, setPaginasLista] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!tel.trim() || nroCliente) {
      setMatches([]);
      setResolviendo(false);
      return undefined;
    }
    let cancel = false;
    setResolviendo(true);
    clienteService
      .getClientesPorTelefono(tel)
      .then((encontrados) => {
        if (cancel) return;
        if (encontrados.length === 1) {
          setSearchParams({ nroCliente: String(encontrados[0].nroCliente), tel }, { replace: true });
          return;
        }
        setMatches(encontrados);
      })
      .catch((error: unknown) => {
        if (!cancel) enqueueSnackbar(getApiErrorMessage(error, 'No se pudo buscar el teléfono.'), { variant: 'error' });
      })
      .finally(() => {
        if (!cancel) setResolviendo(false);
      });
    return () => {
      cancel = true;
    };
  }, [tel, nroCliente, setSearchParams, enqueueSnackbar]);

  const recargarFicha = useCallback(async () => {
    if (!nroCliente) {
      setGestion(null);
      return;
    }
    setCargando(true);
    try {
      const data = await clienteService.getClienteGestion(nroCliente);
      setGestion(data);
      setContactoNota((prev) => prev || data.contacto || '');
    } catch (error: unknown) {
      setGestion(null);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar el cliente.'), { variant: 'error' });
    } finally {
      setCargando(false);
    }
  }, [nroCliente, enqueueSnackbar]);

  const recargarNotas = useCallback(async () => {
    if (!nroCliente) {
      setNotas([]);
      return;
    }
    setCargandoNotas(true);
    try {
      const limite = alcanceNotas === 'todas' ? undefined : Number(alcanceNotas);
      setNotas(await clienteService.getNotasLlamada(nroCliente, limite));
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar las notas.'), { variant: 'error' });
    } finally {
      setCargandoNotas(false);
    }
  }, [nroCliente, alcanceNotas, enqueueSnackbar]);

  const cerrarOperacion = useCallback(() => {
    setOperacion(null);
    void recargarFicha();
    void recargarNotas();
  }, [recargarFicha, recargarNotas]);

  useEffect(() => {
    setOperacion(null);
    setGestion(null);
    setContactoNota('');
    setTextoNota('');
    setIdAccion('');
    setFechaProx('');
    setNotaFormAbierta(false);
    setAplicarSaldosAbierto(false);
    setCreditoAplicar(null);
    setPaginasLista({});
    setRemitoMenu(null);
    void recargarFicha();
  }, [recargarFicha]);

  useEffect(() => {
    void recargarNotas();
  }, [recargarNotas]);

  useEffect(() => {
    let cancel = false;
    clienteService
      .getAccionesNota()
      .then((items) => {
        if (!cancel) setAccionesNota(items);
      })
      .catch(() => {
        if (!cancel) setAccionesNota([]);
      });
    return () => {
      cancel = true;
    };
  }, []);

  const elegirCliente = (id: number) => {
    setBuscador(false);
    const next = new URLSearchParams();
    next.set('nroCliente', String(id));
    if (tel) next.set('tel', tel);
    setSearchParams(next, { replace: true });
  };

  const quitarCliente = () => {
    setOperacion(null);
    setBuscador(false);
    setMatches([]);
    setSearchParams({}, { replace: true });
  };

  const cambiarModo = (_: React.MouseEvent<HTMLElement>, value: Modo | null) => {
    if (!value) return;
    setModo(value);
    saveInfo360Tab('gestion', user?.username, tenantId, value);
  };

  const facturarPedido = (pedidoId?: number | null) => {
    if (!pedidoId || pedidoId <= 0) return;
    setOperacion({ tipo: 'factura', pedidoId });
  };

  const prepararFacturaRemito = async (nroFormulario: number) => {
    const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
    const detalles = factura.detalles ?? [];
    const nroClienteFactura = factura.nroCliente ?? 0;
    if (detalles.length === 0) {
      throw new Error('La factura no tiene ítems para generar el remito.');
    }
    if (nroClienteFactura <= 0) {
      throw new Error('La factura debe tener un cliente asignado.');
    }
    const [sucVentaMap, ubicacionNroSucMap] = await Promise.all([
      cargarMapaSucVentaPorClave(),
      cargarMapaUbicacionNroSuc(),
    ]);
    const ctx = resolverContextoSucVentaImpresion(
      facturaInputDesdeFacturaVenta(factura),
      ubicacionNroSucMap,
      sucVentaMap,
      'cualquiera',
    );
    const sucVenta = await sucVentaService.getSucVenta(ctx.nroSuc, ctx.nroTipoFormulario);
    if (sucVenta.generaRemito !== 'S') {
      throw new Error('Esta sucursal no permite generar remitos desde facturas.');
    }
    return { factura, sucVenta, detalles, nroClienteFactura };
  };

  const remitirFactura = async (nroFormulario?: number | null) => {
    if (!nroFormulario || nroFormulario <= 0) return;
    setRemitiendo(nroFormulario);
    try {
      const { factura, sucVenta, detalles, nroClienteFactura } = await prepararFacturaRemito(nroFormulario);
      if ((factura.nroFormularioRemito ?? 0) > 0) {
        enqueueSnackbar('Esta factura ya tiene un remito generado.', { variant: 'info' });
        return;
      }
      const ahora = new Date();
      const payload: RemitoVentaCreateDto = {
        formulario: {
          nroCliente: nroClienteFactura,
          nombre: factura.nombre,
          cuit: factura.cuit,
          nroLista: factura.nroLista,
          nroSuc: sucVenta.nroSucRemito ?? factura.nroSuc ?? 0,
          descripcion: factura.descripcion ?? `Remito desde factura ${nroFormulario}`,
          nroFormularioFactura: nroFormulario,
          fechaEmision: ahora.toISOString(),
          horaEmision: ahora.toISOString(),
          fechaVencim: ahora.toISOString(),
          nroUbicacionOrigen: factura.nroUbicacionOrigen ?? undefined,
          porcDesc: factura.porcDesc ?? 0,
          porcDesc2: factura.porcDesc2 ?? 0,
          porcDesc3: factura.porcDesc3 ?? 0,
        },
        items: detalles.map((d) => ({
          codigoArticulo: String(d.codigoArticulo ?? '').trim(),
          descripcion: d.descripcionArticulo ?? '',
          cantidad: Number(d.cantidad ?? 0),
          precioUnitario: resolverPrecioListaDetalle(d),
          porcDesc: d.porcDesc ?? 0,
          notas: d.notas ?? '',
          um: String(d.um ?? '').trim() || undefined,
          nroFormularioMaster: resolveMasterNumber(d, [
            'nroFormularioMaster',
            'nroformulariomaster',
            'NroFormularioMaster',
            'nro_formulario_master',
          ]),
          nroItemMaster: resolveMasterNumber(d, [
            'nroItemMaster',
            'nroitemmaster',
            'NroItemMaster',
            'nro_item_master',
          ]),
        })),
      };
      const remitoCreado = await remitoVentaService.createRemitoVenta(payload);
      enqueueSnackbar(`Remito generado (Nro: ${remitoCreado.nroFormulario}).`, { variant: 'success' });
      void recargarFicha();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar el remito.'), { variant: 'error' });
    } finally {
      setRemitiendo(null);
    }
  };

  const abrirRemitoParcial = async (nroFormulario: number) => {
    setRemitiendo(nroFormulario);
    try {
      const { factura, sucVenta, detalles, nroClienteFactura } = await prepararFacturaRemito(nroFormulario);
      const hiloCache = new Map<string, Awaited<ReturnType<typeof ventasSeguimientoService.getHilo>>>();
      const items: RemitoParcialDesdeFactura['items'] = [];
      for (let index = 0; index < detalles.length; index += 1) {
        const detalle = detalles[index];
        const master = resolveDetalleMasterKeys(detalle, {
          fallbackFormulario: nroFormulario,
          fallbackItem: Number(detalle.nroItem ?? index + 1),
        });
        if (!master) continue;
        const clave = `${master.nroFormularioMaster}-${master.nroItemMaster}`;
        if (!hiloCache.has(clave)) {
          hiloCache.set(clave, await ventasSeguimientoService.getHilo(master.nroFormularioMaster, master.nroItemMaster));
        }
        let remitidaStock = 0;
        let devueltaDoc = 0;
        for (const comprobante of hiloCache.get(clave)?.comprobantes || []) {
          const tipo = String(comprobante.nroTipoFormulario ?? '').trim();
          const cantidad = Number(comprobante.cantidad ?? 0);
          if (tipo === TIPO_REMITO_HILO) remitidaStock += cantidad;
          if (tipo === TIPO_NC_HILO) devueltaDoc += cantidad;
        }
        const codigo = String(detalle.codigoArticulo ?? '').trim();
        const um = String(detalle.um ?? '').trim();
        const cantidadDoc = Number(detalle.cantidad ?? 0);
        const facturadaStock = await cantidadLineaAUmStock(codigo, cantidadDoc, um);
        const devueltaStock = await cantidadLineaAUmStock(codigo, devueltaDoc, um);
        const pendienteStock = Math.max(0, facturadaStock - devueltaStock - remitidaStock);
        if (!(pendienteStock > 0)) continue;
        const pendienteDoc = facturadaStock > 0 ? (cantidadDoc * pendienteStock) / facturadaStock : pendienteStock;
        items.push({
          nroFormularioMaster: master.nroFormularioMaster,
          nroItemMaster: master.nroItemMaster,
          codigoArticulo: codigo,
          descripcionArticulo: detalle.descripcionArticulo ?? '',
          cantidad: Number(pendienteDoc),
          precioUnitario: resolverPrecioListaDetalle(detalle),
          porcDesc: Number(detalle.porcDesc ?? 0),
          notas: detalle.notas ?? '',
          um: um || undefined,
        });
      }
      if (items.length === 0) {
        enqueueSnackbar('No hay ítems con pendiente para generar un remito parcial.', { variant: 'warning' });
        return;
      }
      setOperacion({
        tipo: 'remito',
        parcial: {
          nroFormularioFactura: nroFormulario,
          formulario: {
            nroCliente: nroClienteFactura,
            nombre: factura.nombre,
            cuit: factura.cuit,
            nroLista: factura.nroLista,
            nroSuc: sucVenta.nroSucRemito ?? factura.nroSuc ?? 0,
            descripcion: factura.descripcion ?? `Remito parcial desde factura ${nroFormulario}`,
            nroUbicacionOrigen: factura.nroUbicacionOrigen ?? undefined,
            porcDesc: factura.porcDesc ?? 0,
            porcDesc2: factura.porcDesc2 ?? 0,
            porcDesc3: factura.porcDesc3 ?? 0,
          },
          items,
        },
      });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo preparar el remito parcial.'), { variant: 'error' });
    } finally {
      setRemitiendo(null);
    }
  };

  const guardarNota = async () => {
    if (!puedeNota || !nroCliente) return;
    const texto = textoNota.trim();
    if (!texto) {
      enqueueSnackbar('Escribí la nota antes de guardarla.', { variant: 'warning' });
      return;
    }
    setGuardandoNota(true);
    try {
      await clienteService.crearNotaLlamada(nroCliente, {
        texto,
        idAccion: idAccion || null,
        fechaProx: fechaProx || null,
        contacto: contactoNota.trim() || null,
        idLlamante: tel || null,
      });
      setTextoNota('');
      setNotaFormAbierta(false);
      enqueueSnackbar('Nota guardada.', { variant: 'success' });
      await recargarNotas();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo guardar la nota.'), { variant: 'error' });
    } finally {
      setGuardandoNota(false);
    }
  };

  const esNotaDebito = (doc: ClienteGestionDocumento | null) => doc?.tipoCodigo === '2';
  const esNotaCredito = (doc: ClienteGestionDocumento | null) => doc?.tipoCodigo === '3';
  const esRecibo = (doc: ClienteGestionDocumento | null) => doc?.tipoCodigo === '4';
  const esCotizacion = (doc: ClienteGestionDocumento | null) => doc?.tipoCodigo === '10';
  const esPedido = (doc: ClienteGestionDocumento | null) => doc?.tipoCodigo === '12';

  const textoReenvio = (doc: ClienteGestionDocumento | null) => {
    if (esPedido(doc)) {
      return {
        ok: 'Pedido enviado al email del cliente.',
        error: 'No se pudo enviar el pedido.',
        sinEmail: 'El cliente no tiene un email válido para reenviar el pedido.',
        titulo: '¿Enviar pedido por mail?',
      };
    }
    if (esCotizacion(doc)) {
      return {
        ok: 'Cotización enviada al email del cliente.',
        error: 'No se pudo enviar la cotización.',
        sinEmail: 'El cliente no tiene un email válido para reenviar la cotización.',
        titulo: '¿Enviar cotización por mail?',
      };
    }
    if (esNotaCredito(doc)) {
      return {
        ok: 'Nota de crédito enviada al email del cliente.',
        error: 'No se pudo enviar la nota de crédito.',
        sinEmail: 'El cliente no tiene un email válido para reenviar la nota de crédito.',
        titulo: '¿Enviar nota de crédito por mail?',
      };
    }
    if (esRecibo(doc)) {
      return {
        ok: 'Recibo enviado al email del cliente.',
        error: 'No se pudo enviar el recibo.',
        sinEmail: 'El cliente no tiene un email válido para reenviar el recibo.',
        titulo: '¿Enviar recibo por mail?',
      };
    }
    if (esNotaDebito(doc)) {
      return {
        ok: 'Nota de débito enviada al email del cliente.',
        error: 'No se pudo enviar la nota de débito.',
        sinEmail: 'El cliente no tiene un email válido para reenviar la nota de débito.',
        titulo: '¿Enviar nota de débito por mail?',
      };
    }
    return {
      ok: 'Factura enviada al email del cliente.',
      error: 'No se pudo enviar la factura.',
      sinEmail: 'El cliente no tiene un email válido para reenviar la factura.',
      titulo: '¿Enviar factura por mail?',
    };
  };

  const confirmarReenvio = async () => {
    if (!reenviar?.nroFormulario) return;
    const textos = textoReenvio(reenviar);
    setEnviandoMail(true);
    try {
      if (esPedido(reenviar)) {
        await pedidoVentaService.enviarPedidoPorEmailAlCliente(reenviar.nroFormulario);
      } else if (esCotizacion(reenviar)) {
        await cotizacionVentaService.enviarCotizacionPorEmailAlCliente(reenviar.nroFormulario);
      } else if (esNotaCredito(reenviar)) {
        await notaCreditoVentaService.enviarNotaCreditoPorEmailAlCliente(reenviar.nroFormulario);
      } else if (esRecibo(reenviar)) {
        await tesoreriaService.enviarReciboPorEmailAlCliente(reenviar.nroFormulario);
      } else if (esNotaDebito(reenviar)) {
        await notaDebitoVentaService.enviarNotaDebitoPorEmailAlCliente(reenviar.nroFormulario);
      } else {
        await facturaVentaService.enviarFacturaPorEmailAlCliente(reenviar.nroFormulario);
      }
      enqueueSnackbar(textos.ok, { variant: 'success' });
      setReenviar(null);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, textos.error), { variant: 'error' });
    } finally {
      setEnviandoMail(false);
    }
  };

  const pedirReenvio = (doc: ClienteGestionDocumento) => {
    const email = gestion?.email?.trim();
    if (!email || !email.includes('@')) {
      enqueueSnackbar(textoReenvio(doc).sinEmail, { variant: 'warning' });
      return;
    }
    setReenviar(doc);
  };

  const cerrarPdf = () => {
    setPdf((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const abrirPdf = async (doc: ClienteGestionDocumento) => {
    if (!doc.nroFormulario) return;
    setAbriendoPdf(doc.nroFormulario);
    try {
      let url: string;
      let documentType: PdfVista['documentType'];
      if (doc.tipoCodigo === '1') {
        const pdfFactura = await fetchFacturaVentaPdf(doc.nroFormulario);
        if (pdfFactura.avisoImpresion) {
          enqueueSnackbar(pdfFactura.avisoImpresion, { variant: 'warning' });
        }
        url = pdfFactura.url;
        documentType = 'factura';
      } else if (doc.tipoCodigo === '2') {
        const blob = await notaDebitoVentaService.getNotaDebitoVentaPDF(doc.nroFormulario);
        url = window.URL.createObjectURL(blob);
        documentType = 'nota-debito';
      } else if (doc.tipoCodigo === '12') {
        const blob = await pedidoVentaService.getPedidoVentaPDF(doc.nroFormulario);
        url = window.URL.createObjectURL(blob);
        documentType = 'pedido';
      } else if (doc.tipoCodigo === '10') {
        const blob = await cotizacionVentaService.getCotizacionVentaPDF(doc.nroFormulario);
        url = window.URL.createObjectURL(blob);
        documentType = 'cotizacion';
      } else if (doc.tipoCodigo === '9') {
        const response = await api.get<Blob>(`/api/RemitosVenta/${doc.nroFormulario}/pdf`, {
          responseType: 'blob',
        });
        url = window.URL.createObjectURL(response.data);
        documentType = 'remito';
      } else if (doc.tipoCodigo === '3') {
        const blob = await notaCreditoVentaService.getNotaCreditoVentaPDF(doc.nroFormulario);
        url = window.URL.createObjectURL(blob);
        documentType = 'nota-credito';
      } else if (doc.tipoCodigo === '4') {
        const response = await api.get<Blob>(`/api/tesoreria/recibos/${doc.nroFormulario}/pdf`, {
          responseType: 'blob',
        });
        url = window.URL.createObjectURL(response.data);
        documentType = 'recibo';
      } else {
        enqueueSnackbar('Este comprobante no tiene impresión.', { variant: 'info' });
        return;
      }
      setPdf((prev) => {
        if (prev) window.URL.revokeObjectURL(prev.url);
        return {
          url,
          title: `${doc.tipo} ${doc.numero}`.trim(),
          documentId: String(doc.nroFormulario),
          documentType,
        };
      });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo imprimir el comprobante.'), { variant: 'error' });
    } finally {
      setAbriendoPdf(null);
    }
  };

  const abrirDocumento = (doc: ClienteGestionDocumento) => {
    if (!doc.nroFormulario) return;
    if (doc.tipoCodigo === '1') {
      setOperacion({ tipo: 'factura', nroFormulario: doc.nroFormulario });
      return;
    }
    if (doc.tipoCodigo === '10') {
      abrirPestana(ROUTES.ventas.cotizaciones.ver(doc.nroFormulario));
      return;
    }
    if (doc.tipoCodigo === '3') {
      abrirPestana(ROUTES.ventas.notasCredito.ver(doc.nroFormulario));
      return;
    }
    if (doc.tipoCodigo === '2') {
      abrirPestana(ROUTES.ventas.notasDebito.ver(doc.nroFormulario));
      return;
    }
    void abrirPdf(doc);
  };

  const accionSeleccionada = useMemo(
    () => accionesNota.find((a) => a.idAccion === idAccion) ?? null,
    [accionesNota, idAccion],
  );

  const nombre = (gestion?.razonSocial || gestion?.nombre || '').trim();
  const tamanoLista = 5;
  const topeLista = (clave: string) => (paginasLista[clave] ?? 1) * tamanoLista;
  const recortar = (clave: string, items: ClienteGestionDocumento[]) => items.slice(0, topeLista(clave));
  const botonMas = (clave: string, total: number) => (
    total > topeLista(clave) ? (
      <Button
        size="small"
        onClick={() => setPaginasLista((prev) => ({ ...prev, [clave]: (prev[clave] ?? 1) + 1 }))}
        sx={{ textTransform: 'none', mt: 0.5 }}
      >
        Mostrar más
      </Button>
    ) : null
  );
  const info360Severidad: Info360Severidad | null = gestion?.recomendacionSeveridad === 'danger'
    || gestion?.recomendacionSeveridad === 'warning'
    || gestion?.recomendacionSeveridad === 'ok'
    || gestion?.recomendacionSeveridad === 'info'
    ? gestion.recomendacionSeveridad
    : null;
  const severidad = gestion?.recomendacionSeveridad === 'danger'
    ? 'error'
    : gestion?.recomendacionSeveridad === 'warning'
      ? 'warning'
      : 'success';

  const boton = (label: string, onClick: () => void, enabled = true) => (
    <Button
      key={label}
      size="small"
      variant="outlined"
      disabled={!enabled}
      onClick={onClick}
      sx={{ textTransform: 'none' }}
    >
      {label}
    </Button>
  );

  const icono = (
    label: string,
    icon: React.ReactNode,
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    enabled = true,
  ) => {
    const [bg, hover] = coloresAccion[label] ?? ['#607d8b', '#546e7a'];
    return (
      <Tooltip key={label} title={label}>
        <span>
          <IconButton
            size="small"
            aria-label={label}
            disabled={!enabled}
            onClick={onClick}
            sx={{
              bgcolor: bg,
              color: '#fff',
              borderRadius: 1.5,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: hover, transform: 'scale(1.1)' },
              '&.Mui-disabled': { bgcolor: '#ccc', color: '#fff' },
              transition: 'all 0.2s',
            }}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const imprimir = (doc: ClienteGestionDocumento) =>
    icono(
      'Imprimir',
      <PrintIcon fontSize="small" />,
      () => { void abrirPdf(doc); },
      Boolean(doc.nroFormulario) && abriendoPdf !== doc.nroFormulario,
    );

  const formularioOperacion = operacion && nroCliente ? (
    <GestionCerrarContext.Provider value={cerrarOperacion}>
      {operacion.tipo === 'cotizacion' ? (
        <CotizacionesVenta key={`cot-${nroCliente}`} nroClienteInicial={nroCliente} />
      ) : null}
      {operacion.tipo === 'pedido' ? (
        <PedidosVenta key={`ped-${nroCliente}`} nroClienteInicial={nroCliente} />
      ) : null}
      {operacion.tipo === 'factura' ? (
        <NuevaFacturaPOS
          key={`fac-${nroCliente}-${operacion.pedidoId ?? 0}-${operacion.nroFormulario ?? 0}-${operacion.cotizacionId ?? 0}`}
          pedidoIdInicial={operacion.pedidoId}
          nroFormularioInicial={operacion.nroFormulario}
          cotizacionIdInicial={operacion.cotizacionId}
        />
      ) : null}
      {operacion.tipo === 'remito' ? (
        <RemitoVentaForm
          key={`rem-${nroCliente}-${operacion.parcial?.nroFormularioFactura ?? 0}`}
          nroClienteInicial={nroCliente}
          remitoParcialInicial={operacion.parcial}
        />
      ) : null}
      {operacion.tipo === 'nc' ? (
        <NuevaNotaCreditoPOS key={`nc-${nroCliente}`} nroClienteInicial={nroCliente} />
      ) : null}
      {operacion.tipo === 'nd' ? (
        <NuevaNotaDebitoPOS key={`nd-${nroCliente}`} nroClienteInicial={nroCliente} />
      ) : null}
      {operacion.tipo === 'recibo' ? (
        <NuevoRecibo
          key={`rec-${nroCliente}-${operacion.nroFormulario ?? 0}`}
          nroClienteInicial={nroCliente}
          nroFormularioAplicar={operacion.nroFormulario}
        />
      ) : null}
    </GestionCerrarContext.Provider>
  ) : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {formularioOperacion ?? (!nroCliente ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                ¿Con quién querés trabajar hoy?
              </Typography>
              {resolviendo ? <CircularProgress size={28} /> : null}
              {!resolviendo && tel && matches.length === 0 ? (
                <Alert severity="info">
                  Ningún cliente coincide con este teléfono. Buscalo por nombre, CUIT o número.
                </Alert>
              ) : null}
              {matches.length > 1 ? (
                <Stack spacing={1}>
                  <Typography variant="body2">Hay más de un cliente con este teléfono. Elegí uno.</Typography>
                  {matches.map((m) => (
                    <Button
                      key={m.nroCliente}
                      variant="outlined"
                      onClick={() => elegirCliente(m.nroCliente)}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      {m.razonSocial || m.nombre} · #{m.nroCliente}
                      {m.telefono ? ` · ${m.telefono}` : ''}
                    </Button>
                  ))}
                </Stack>
              ) : null}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                  <FacturaPOSClienteSearchBar
                    onClienteSelect={(cliente) => elegirCliente(cliente.nroCliente)}
                    onClienteInactivo={(mensaje) => enqueueSnackbar(mensaje, { variant: 'warning' })}
                  />
                </Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PersonAdd />}
                    onClick={() => setNuevoClienteAbierto(true)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    Nuevo cliente
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PersonSearch color="primary" />}
                    onClick={() => setBuscador(true)}
                    sx={{
                      py: 1,
                      px: 2,
                      borderRadius: 2,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      bgcolor: 'background.paper',
                      borderColor: 'divider',
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    Seleccionar cliente
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {cargando && !nombre ? 'Cargando…' : nombre || `Cliente #${nroCliente}`}
                </Typography>
                <Tooltip
                  title={
                    gestion?.recomendacionTitulo
                      ? `Info 360 · ${gestion.recomendacionTitulo}`
                      : 'Info 360 del cliente'
                  }
                >
                  <IconButton
                    size="small"
                    aria-label={
                      gestion?.recomendacionTitulo
                        ? `Info 360 del cliente: ${gestion.recomendacionTitulo}`
                        : 'Info 360 del cliente'
                    }
                    onClick={() => navigate(ROUTES.ventas.clientes.info(nroCliente))}
                    sx={{
                      color: colorSemaforoInfo360(info360Severidad),
                      ...(info360Severidad === 'danger' ? {
                        animation: 'info360Riesgo 1.4s ease-in-out infinite',
                        '@keyframes info360Riesgo': {
                          '0%, 100%': {
                            transform: 'scale(1)',
                            filter: 'drop-shadow(0 0 0 rgba(211,47,47,0))',
                          },
                          '50%': {
                            transform: 'scale(1.18)',
                            filter: 'drop-shadow(0 0 5px rgba(211,47,47,0.75))',
                          },
                        },
                        '@media (prefers-reduced-motion: reduce)': {
                          animation: 'none',
                        },
                      } : {}),
                    }}
                  >
                    <TravelExploreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cambiar cliente">
                  <IconButton aria-label="Cambiar cliente" onClick={() => setBuscador(true)} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Quitar cliente">
                  <IconButton aria-label="Quitar cliente" color="error" onClick={quitarCliente} size="small">
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                #{nroCliente}
                {gestion?.cuit ? ` · CUIT ${gestion.cuit}` : ''}
                {gestion?.contacto ? ` · Contacto ${gestion.contacto}` : ''}
                {gestion?.condVta ? ` · ${gestion.condVta}` : ''}
                {gestion?.vendedor ? ` · ${gestion.vendedor}` : ''}
                {gestion?.listaDescripcion || gestion?.nroLista
                  ? ` · Lista ${gestion.listaDescripcion || gestion.nroLista}`
                  : ''}
              </Typography>

              {gestion ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Saldo ${formatCurrency(gestion.saldoTotal)}`} />
                  <Chip color={gestion.saldoVencido > 0 ? 'warning' : 'default'} label={`Vencido ${formatCurrency(gestion.saldoVencido)}`} />
                  <Chip label={`Disponible ${formatCurrency(gestion.creditoDisponible)}`} />
                  <Chip
                    label={`Cheques devueltos 12 meses: ${gestion.chequesDevueltosCantidad} · ${formatCurrency(gestion.chequesDevueltosImporte)}`}
                    onClick={() => navigate(ROUTES.ventas.clientes.info(nroCliente))}
                  />
                  {gestion.proximaVisita ? <Chip label={`Próxima visita ${gestion.proximaVisita}`} /> : null}
                </Stack>
              ) : null}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(ROUTES.ventas.clientes.editar(nroCliente))}
                  sx={{ textTransform: 'none' }}
                >
                  Editar maestro
                </Button>
                <Button
                  variant="contained"
                  onClick={() => abrirPestana(`/ventas/informes/resumen-cuenta/${nroCliente}`)}
                  sx={{ textTransform: 'none' }}
                >
                  Cuenta corriente
                </Button>
              </Stack>

              {gestion?.recomendacionTitulo ? (
                <Alert severity={severidad}>
                  <strong>{gestion.recomendacionTitulo}.</strong> {gestion.recomendacionDetalle}
                </Alert>
              ) : null}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Notas
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={alcanceNotas}
                      onChange={(_, value: '3' | '10' | 'todas' | null) => {
                        if (value) setAlcanceNotas(value);
                      }}
                    >
                      <ToggleButton value="3" sx={{ textTransform: 'none' }}>Últimas 3</ToggleButton>
                      <ToggleButton value="10" sx={{ textTransform: 'none' }}>Últimas 10</ToggleButton>
                      <ToggleButton value="todas" sx={{ textTransform: 'none' }}>Todas</ToggleButton>
                    </ToggleButtonGroup>
                    {cargandoNotas ? <CircularProgress size={16} /> : null}
                  </Stack>
                  {notas.length === 0 && !cargandoNotas ? (
                    <Typography variant="body2" color="text.secondary">Todavía no hay notas de este cliente.</Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      {notas.map((nota) => (
                        <Typography key={nota.nroNota} variant="body2">
                          {fechaHoraNota(nota.fechaEmision, nota.horaEmision)}
                          {' · Escribió '}
                          {nota.usuario || '—'}
                          {' · Contacto '}
                          {nota.contacto || '—'}
                          {nota.accionDescripcion ? ` · ${nota.accionDescripcion}` : ''}
                          {nota.texto ? `. ${nota.texto}` : ''}
                          {nota.fechaProx ? ` Seguimiento ${fechaCorta(nota.fechaProx)}.` : ''}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Box>
                <Stack spacing={1.5}>
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={() => setNotaFormAbierta((abierta) => !abierta)}
                      sx={{ textTransform: 'none' }}
                    >
                      Nueva nota
                    </Button>
                  </Box>
                  <Collapse in={notaFormAbierta} unmountOnExit>
                  <Stack spacing={1.5}>
                  <TextField
                    select
                    label="Acción"
                    size="small"
                    value={idAccion}
                    onChange={(e) => {
                      const id = e.target.value;
                      setIdAccion(id);
                      const accion = accionesNota.find((a) => a.idAccion === id);
                      if (accion?.diasSeguimiento && accion.diasSeguimiento > 0) {
                        setFechaProx(hoyMas(accion.diasSeguimiento));
                      }
                    }}
                    fullWidth
                  >
                    <MenuItem value="">Sin acción</MenuItem>
                    {accionesNota.map((accion) => (
                      <MenuItem key={accion.idAccion} value={accion.idAccion}>
                        {accion.descripcion || accion.idAccion}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Contacto"
                    size="small"
                    value={contactoNota}
                    onChange={(e) => setContactoNota(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Seguimiento"
                    type="date"
                    size="small"
                    value={fechaProx}
                    onChange={(e) => setFechaProx(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText={accionSeleccionada?.diasSeguimiento ? `La acción propone ${accionSeleccionada.diasSeguimiento} días.` : ' '}
                    fullWidth
                  />
                  <TextField
                    label="Nota"
                    multiline
                    minRows={2}
                    value={textoNota}
                    onChange={(e) => setTextoNota(e.target.value)}
                    fullWidth
                  />
                  {puedeNota && (
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => { void guardarNota(); }}
                      disabled={guardandoNota}
                      sx={{ textTransform: 'none' }}
                    >
                      {guardandoNota ? 'Guardando…' : 'Guardar nota'}
                    </Button>
                  </Box>
                  )}
                  </Stack>
                  </Collapse>
                </Stack>
              </Box>

              <ToggleButtonGroup exclusive value={modo} onChange={cambiarModo} size="small">
                <ToggleButton value="vender" sx={{ textTransform: 'none', px: 2 }}>Vender</ToggleButton>
                <ToggleButton value="cobrar" sx={{ textTransform: 'none', px: 2 }}>Cobrar</ToggleButton>
              </ToggleButtonGroup>

              <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {modo === 'vender' ? (
                      <>
                        {boton('Cotización', () => setOperacion({ tipo: 'cotizacion' }), puedeCotizacion)}
                        {boton('Pedido', () => setOperacion({ tipo: 'pedido' }), puedePedido)}
                        {boton('Factura', () => setOperacion({ tipo: 'factura' }), puedeFactura)}
                        {boton('Remito', () => setOperacion({ tipo: 'remito' }), puedeRemito)}
                        {boton('Nota de crédito', () => setOperacion({ tipo: 'nc' }), puedeNc)}
                        {boton('Nota de débito', () => setOperacion({ tipo: 'nd' }), puedeNd)}
                      </>
                    ) : (
                      <>
                        {boton('Recibo de cobro', () => setOperacion({ tipo: 'recibo' }))}
                        {boton('Aplicar saldo', () => {
                          setCreditoAplicar(null);
                          setAplicarSaldosAbierto(true);
                        })}
                      </>
                    )}
                  </Stack>

                  {modo === 'vender' ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 3,
                      alignItems: 'start',
                    }}
                  >
                    <Seccion titulo="Pedidos abiertos">
                      {(gestion?.pedidosAbiertos ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No hay pedidos pendientes de facturar.</Typography>
                      ) : (
                        <>
                          {recortar('pedidos', gestion?.pedidosAbiertos ?? []).map((doc) => (
                            <FilaDocumento
                              key={`ped-${doc.nroFormulario ?? doc.numero}`}
                              doc={doc}
                              onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                              acciones={(
                                <>
                                  {imprimir(doc)}
                                  {icono('Facturar', <ReceiptIcon fontSize="small" />, () => facturarPedido(doc.nroFormulario), puedeFactura)}
                                  {icono('Reenviar', <ForwardToInboxIcon fontSize="small" />, () => pedirReenvio(doc), Boolean(doc.nroFormulario))}
                                </>
                              )}
                            />
                          ))}
                          {botonMas('pedidos', gestion?.pedidosAbiertos.length ?? 0)}
                        </>
                      )}
                    </Seccion>
                    <Seccion titulo="Últimas facturas">
                      {(gestion?.ultimasFacturas ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No hay facturas recientes.</Typography>
                      ) : (
                        <>
                          {recortar('facturas', gestion?.ultimasFacturas ?? []).map((doc) => (
                            <FilaDocumento
                              key={`fac-${doc.nroFormulario ?? doc.numero}`}
                              doc={doc}
                              onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                              acciones={(
                                <>
                                  {imprimir(doc)}
                                  {icono(
                                    'Remitir',
                                    <LocalShippingIcon fontSize="small" />,
                                    (event) => {
                                      if (!doc.nroFormulario) return;
                                      setRemitoMenu({ el: event.currentTarget, facturaId: doc.nroFormulario });
                                    },
                                    puedeRemito && Boolean(doc.nroFormulario) && remitiendo !== doc.nroFormulario,
                                  )}
                                  {doc.saldo > 0.009 ? icono('Cobrar', <PaymentsIcon fontSize="small" />, () => {
                                    if (!doc.nroFormulario) return;
                                    setOperacion({ tipo: 'recibo', nroFormulario: doc.nroFormulario });
                                  }, Boolean(doc.nroFormulario)) : null}
                                  {icono('Reenviar', <ForwardToInboxIcon fontSize="small" />, () => pedirReenvio(doc), Boolean(doc.nroFormulario))}
                                </>
                              )}
                            />
                          ))}
                          {botonMas('facturas', gestion?.ultimasFacturas.length ?? 0)}
                        </>
                      )}
                    </Seccion>
                    <Seccion titulo="Cotizaciones">
                      {(gestion?.cotizaciones ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No hay cotizaciones recientes.</Typography>
                      ) : (
                        <>
                          {recortar('cotizaciones', gestion?.cotizaciones ?? []).map((doc) => (
                            <FilaDocumento
                              key={`cot-${doc.nroFormulario ?? doc.numero}`}
                              doc={doc}
                              onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                              acciones={(
                                <>
                                  {imprimir(doc)}
                                  {icono('Facturar', <ReceiptIcon fontSize="small" />, () => {
                                    if (doc.nroFormulario) setOperacion({ tipo: 'factura', cotizacionId: doc.nroFormulario });
                                  }, puedeFactura && Boolean(doc.nroFormulario))}
                                  {icono('Reenviar', <ForwardToInboxIcon fontSize="small" />, () => pedirReenvio(doc), Boolean(doc.nroFormulario))}
                                </>
                              )}
                            />
                          ))}
                          {botonMas('cotizaciones', gestion?.cotizaciones.length ?? 0)}
                        </>
                      )}
                    </Seccion>
                    <Seccion titulo="Remitos">
                      {(gestion?.remitos ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No hay remitos recientes.</Typography>
                      ) : (
                        <>
                          {recortar('remitos', gestion?.remitos ?? []).map((doc) => (
                            <FilaDocumento
                              key={`rem-${doc.nroFormulario ?? doc.numero}`}
                              doc={doc}
                              onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                              acciones={imprimir(doc)}
                            />
                          ))}
                          {botonMas('remitos', gestion?.remitos.length ?? 0)}
                        </>
                      )}
                    </Seccion>
                    <Seccion titulo="Notas de crédito">
                      {(gestion?.notasCredito ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No hay notas de crédito recientes.</Typography>
                      ) : (
                        <>
                          {recortar('nc', gestion?.notasCredito ?? []).map((doc) => (
                            <FilaDocumento
                              key={`nc-${doc.nroFormulario ?? doc.numero}`}
                              doc={doc}
                              onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                              acciones={(
                                <>
                                  {imprimir(doc)}
                                  {icono('Aplicar', <SwapHorizIcon fontSize="small" />, () => {
                                    if (!doc.nroFormulario) return;
                                    setCreditoAplicar({
                                      nroFormulario: doc.nroFormulario,
                                      tipoCodigo: doc.tipoCodigo || '3',
                                      numero: doc.numero,
                                      importe: doc.importe || doc.saldo,
                                    });
                                  }, Boolean(doc.nroFormulario))}
                                  {icono('Reenviar', <ForwardToInboxIcon fontSize="small" />, () => pedirReenvio(doc), Boolean(doc.nroFormulario))}
                                </>
                              )}
                            />
                          ))}
                          {botonMas('nc', gestion?.notasCredito.length ?? 0)}
                        </>
                      )}
                    </Seccion>
                    <Seccion titulo="Notas de débito">
                      {(gestion?.notasDebito ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No hay notas de débito recientes.</Typography>
                      ) : (
                        <>
                          {recortar('nd', gestion?.notasDebito ?? []).map((doc) => (
                            <FilaDocumento
                              key={`nd-${doc.nroFormulario ?? doc.numero}`}
                              doc={doc}
                              onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                              acciones={(
                                <>
                                  {imprimir(doc)}
                                  {icono('Cobrar', <PaymentsIcon fontSize="small" />, () => {
                                    if (!doc.nroFormulario) return;
                                    setOperacion({ tipo: 'recibo', nroFormulario: doc.nroFormulario });
                                  }, Boolean(doc.nroFormulario))}
                                  {icono('Reenviar', <ForwardToInboxIcon fontSize="small" />, () => pedirReenvio(doc), Boolean(doc.nroFormulario))}
                                </>
                              )}
                            />
                          ))}
                          {botonMas('nd', gestion?.notasDebito.length ?? 0)}
                        </>
                      )}
                    </Seccion>
                  </Box>
                  ) : null}

                  {modo === 'vender' ? (
                    <Seccion titulo="Suele comprar">
                      {(gestion?.sugerencias ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No hay artículos habituales atrasados.</Typography>
                      ) : (
                        <Stack spacing={1}>
                          {gestion?.sugerencias.map((item) => (
                            <Box
                              key={item.codigoArticulo || item.descripcion}
                              sx={{
                                px: 1.5,
                                py: 1.25,
                                borderRadius: 2,
                                bgcolor: '#fafafa',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                                {item.descripcion}
                              </Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {item.codigoArticulo ? (
                                  <Chip size="small" variant="outlined" label={item.codigoArticulo} />
                                ) : null}
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={item.diasDesdeUltimaCompra >= 90 ? 'warning' : 'default'}
                                  label={`Hace ${item.diasDesdeUltimaCompra} días`}
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Cant. ${item.ultimaCantidad.toLocaleString('es-AR', { maximumFractionDigits: 3 })}`}
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  label={`Último ${formatCurrency(item.ultimoPrecio)}`}
                                />
                                {item.precioLista != null ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    label={`Lista ${formatCurrency(item.precioLista)}`}
                                  />
                                ) : null}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Seccion>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 3,
                        alignItems: 'start',
                      }}
                    >
                      <Seccion titulo="Por cobrar">
                        {(gestion?.porCobrar ?? []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No hay facturas por vencer esta semana ni una deuda antigua destacada.</Typography>
                        ) : (
                          <>
                            {recortar('cobrar', gestion?.porCobrar ?? []).map((doc) => (
                              <FilaDocumento
                                key={`cob-${doc.nroFormulario ?? doc.numero}`}
                                doc={doc}
                                onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                                acciones={(
                                  <>
                                    {imprimir(doc)}
                                    {icono('Cobrar', <PaymentsIcon fontSize="small" />, () => {
                                      if (!doc.nroFormulario) return;
                                      setOperacion({ tipo: 'recibo', nroFormulario: doc.nroFormulario });
                                    }, Boolean(doc.nroFormulario))}
                                    {doc.tipoCodigo === '1' || doc.tipoCodigo === '2'
                                      ? icono('Reenviar', <ForwardToInboxIcon fontSize="small" />, () => pedirReenvio(doc), Boolean(doc.nroFormulario))
                                      : null}
                                  </>
                                )}
                              />
                            ))}
                            {botonMas('cobrar', gestion?.porCobrar.length ?? 0)}
                          </>
                        )}
                      </Seccion>
                      <Seccion titulo="Sin aplicar">
                        {(gestion?.sinAplicar ?? []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No hay notas de crédito ni recibos sin aplicar.</Typography>
                        ) : (
                          <>
                            {recortar('sinaplicar', gestion?.sinAplicar ?? []).map((doc) => (
                              <FilaDocumento
                                key={`sa-${doc.nroFormulario ?? doc.numero}`}
                                doc={doc}
                                onAbrir={doc.nroFormulario ? () => abrirDocumento(doc) : undefined}
                                acciones={(
                                  <>
                                    {imprimir(doc)}
                                    {icono('Aplicar', <SwapHorizIcon fontSize="small" />, () => {
                                      if (!doc.nroFormulario) return;
                                      setCreditoAplicar({
                                        nroFormulario: doc.nroFormulario,
                                        tipoCodigo: doc.tipoCodigo,
                                        numero: doc.numero,
                                        importe: doc.importe || doc.saldo,
                                      });
                                    }, Boolean(doc.nroFormulario))}
                                    {doc.tipoCodigo === '3' || doc.tipoCodigo === '4'
                                      ? icono('Reenviar', <ForwardToInboxIcon fontSize="small" />, () => pedirReenvio(doc), Boolean(doc.nroFormulario))
                                      : null}
                                  </>
                                )}
                              />
                            ))}
                            {botonMas('sinaplicar', gestion?.sinAplicar.length ?? 0)}
                          </>
                        )}
                      </Seccion>
                    </Box>
                  )}
              </Stack>
            </Stack>
          ))}
        </CardContent>
      </Card>

      <ClienteSearch
        open={buscador}
        onClose={() => setBuscador(false)}
        onSelect={(cliente) => elegirCliente(cliente.nroCliente)}
      />

      <FacturaPOSNuevoClienteDialog
        open={nuevoClienteAbierto}
        onClose={() => setNuevoClienteAbierto(false)}
        onCreated={(cliente) => {
          setNuevoClienteAbierto(false);
          elegirCliente(cliente.nroCliente);
        }}
      />

      {(creditoAplicar || aplicarSaldosAbierto) && nroCliente ? (
        <AplicarSaldoReciboDialog
          open
          onClose={() => {
            setCreditoAplicar(null);
            setAplicarSaldosAbierto(false);
          }}
          reciboId={creditoAplicar?.nroFormulario}
          selectorMode={!creditoAplicar}
          nroCliente={nroCliente}
          nombreCliente={nombre || `Cliente ${nroCliente}`}
          nroTipoFormularioCredito={creditoAplicar?.tipoCodigo}
          numeroComprobanteCredito={creditoAplicar?.numero}
          importeTotalCredito={creditoAplicar?.importe}
          onSuccess={() => {
            setCreditoAplicar(null);
            setAplicarSaldosAbierto(false);
            void recargarFicha();
          }}
        />
      ) : null}

      <ConfirmarEnvioFacturaEmailDialog
        open={Boolean(reenviar)}
        sending={enviandoMail}
        clienteTieneEmail={Boolean(gestion?.email?.includes('@'))}
        emailsSugeridos={gestion?.email ? [gestion.email] : []}
        nombreCliente={nombre}
        comprobanteLabel={reenviar ? `${reenviar.tipo} ${reenviar.numero}` : ''}
        titulo={textoReenvio(reenviar).titulo}
        onNoEnviar={() => {
          if (!enviandoMail) setReenviar(null);
        }}
        onEnviar={() => { void confirmarReenvio(); }}
      />

      <Menu
        anchorEl={remitoMenu?.el}
        open={Boolean(remitoMenu)}
        onClose={() => setRemitoMenu(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            const facturaId = remitoMenu?.facturaId;
            setRemitoMenu(null);
            if (facturaId) void remitirFactura(facturaId);
          }}
        >
          <ListItemIcon><LocalShippingIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Generar remito total</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const facturaId = remitoMenu?.facturaId;
            setRemitoMenu(null);
            if (facturaId) void abrirRemitoParcial(facturaId);
          }}
        >
          <ListItemIcon><LocalShippingIcon fontSize="small" sx={{ color: '#f57c00' }} /></ListItemIcon>
          <ListItemText>Generar remito parcial</ListItemText>
        </MenuItem>
      </Menu>

      {pdf ? (
        <PDFViewer
          pdfUrl={pdf.url}
          onClose={cerrarPdf}
          title={pdf.title}
          documentId={pdf.documentId}
          documentType={pdf.documentType}
        />
      ) : null}
    </Box>
  );
};

const Seccion: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{titulo}</Typography>
    <Divider sx={{ mb: 1 }} />
    {children}
  </Box>
);

export default GestionClientes;
